import type { Preferences } from 'api/types/preferences.types';
import { ServiceLine } from 'api/types/service.types';
import { RestorationIcon, RightArrowInstaShop, SoleIcingIcon, StainProtectionIcon } from 'assets/icons';
import OptionItem from 'components/molecules/OptionItem';
import { useConfig } from 'context/app-config';
import {
  selectCurrentOrder,
  selectIsMultipleServicesSelected,
  selectPreference,
  selectPreferenceType,
  selectServiceLineCollectionMethods,
} from 'pages/newOrder/newOrder.selectors';
import { updatePreferences } from 'pages/newOrder/newOrder.slice';
import type { FC, SVGProps } from 'react';
import React, { createElement, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from 'redux-stores/reducers/rootReducer';
import type { CSSObject, DefaultTheme } from 'styled-components';
import { styled } from 'styled-components';

import type { OptionsFormProps } from './OptionsFormSheet';
import OptionsFormSheet from './OptionsFormSheet';
import OptionsRenderer from './OptionsRenderer';

const StyledH4 = styled.h4`
  color: ${({ theme }) => theme.components.tipBox.text.color.unselected};
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
`;

interface IconProps {
  theme: DefaultTheme;
  isActive?: boolean;
  isStroke?: boolean;
  isRemoveStroke?: boolean;
}

const withIconStyles = (IconComponent: FC<SVGProps<any>>) => styled(IconComponent)<IconProps>`
  > circle {
    ${({ theme, isActive }: IconProps): CSSObject => ({
      fill: isActive ? theme.icon?.activeColorBg : theme.icon?.color,
    })};
  }

  > path {
    ${({ theme, isActive, isStroke, isRemoveStroke }: IconProps): CSSObject => ({
      fill: isActive ? theme.icon?.activeColor : theme.icon?.color,
      stroke: isRemoveStroke ? 'unset' : isActive && isStroke ? theme.icon?.activeColor : theme.icon?.color,
    })};
    opacity: 0.5;
  }
`;

const StyledRestorationIcon = withIconStyles(RestorationIcon);
const StyledSoleIcingIcon = withIconStyles(SoleIcingIcon);
const StyledStainProtectionIcon = withIconStyles(StainProtectionIcon);

export type IPreferencesOptions = Preferences.CustomerPreferences & Preferences.ShoeAddOnApprovals;

const OrderInstructionsOptions: React.FC<{ serviceLine?: ServiceLine }> = ({ serviceLine }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const config = useConfig();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [optionType, setOptionType] = useState<keyof IPreferencesOptions>('folding');

  const { isDropoffAtDoor: defaultDropOffAtDoor, isPickupAtDoor } = useSelector(selectCurrentOrder);
  const serviceLineDropoffMethods = useSelector(selectServiceLineCollectionMethods);
  const hasMultipleServicesSelected = useSelector(selectIsMultipleServicesSelected);

  const isDropoffAtDoor = useMemo(
    () => (serviceLine ? serviceLineDropoffMethods[serviceLine]?.isDropoffAtDoor : defaultDropOffAtDoor),
    [defaultDropOffAtDoor, serviceLine, serviceLineDropoffMethods]
  );

  const preferences = useSelector((state: RootState) => selectPreference(state, undefined, serviceLine));

  const foldingPreference = useSelector((state: RootState) => selectPreferenceType(state, 'folding', serviceLine));

  const creasesPreference = useSelector((state: RootState) => selectPreferenceType(state, 'creases', serviceLine));

  const starchPreference = useSelector((state: RootState) => selectPreferenceType(state, 'starch', serviceLine));

  const stainDamageApprovalsPreference = useSelector((state: RootState) => selectPreferenceType(state, 'stainDamageApprovals', serviceLine));

  const driverPickupInstructionsPreference = useSelector((state: RootState) => selectPreferenceType(state, 'driverPickupInstructions', serviceLine));

  const driverDropoffInstructionsPreference = useSelector((state: RootState) =>
    selectPreferenceType(state, 'driverDropoffInstructions', serviceLine)
  );

  const shoeAddOnApprovalsPreferences = useSelector((state: RootState) =>
    selectPreferenceType(state, 'shoeAddOnApprovals', serviceLine)
  ) as Preferences.ShoeAddOnApprovals;

  /**
   * @description render bottom sheet options based on the selected preference
   */
  const optionsBSheetProps = useCallback(
    (type: keyof IPreferencesOptions) => {
      let optionsToReturn;
      switch (type) {
        case 'driverPickupInstructions':
        case 'driverDropoffInstructions':
        case 'folding':
          optionsToReturn = { hideSeparator: true };
          break;
        case 'stainDamageApprovals':
          optionsToReturn = {
            hideCategoryName: true,
            description: t('newOrderPage.autoApprovalActivationNote'),
          };
          break;
        case 'soleIcing':
        case 'stainProtection':
        case 'restoration':
          optionsToReturn = {
            description: t(`newOrderPage.${type}Description`),
          };
          break;
        default:
          optionsToReturn = {};
          break;
      }

      return optionsToReturn;
    },
    [t]
  );

  /**
   * @description render bottom sheet options based on the selected preference
   */
  const renderSheet = useCallback(() => {
    if (!preferences) return undefined;

    // default props
    const Props: OptionsFormProps = {
      show: isSheetOpen,
      onClose: () => setIsSheetOpen(false),
      optionType,
      title: (() => t(`newOrderPage.instructionOptions.${optionType}`))(),
      ...optionsBSheetProps(optionType),
    };

    const getshoeAddOnApprovalsItem = (key: keyof Preferences.ShoeAddOnApprovals) => ({
      categories: [
        {
          key,
          text: t(`newOrderPage.${key}`),
          allowMultiSelect: false,
          options: [
            {
              key,
              text: t(`newOrderPage.${key}Option`),
              value: shoeAddOnApprovalsPreferences ? shoeAddOnApprovalsPreferences[key] : false,
            },
          ],
        },
      ],
    });

    switch (optionType) {
      case 'stainDamageApprovals':
      case 'creases':
      case 'starch':
      case 'folding':
        Props.optionType = optionType;
        Props.preference = preferences[optionType];
        if (optionType === 'folding') {
          if (config?.modules.newOrder.instructions?.supportBadgesLayoutForFolding) {
            Props.checkboxType = 'badge';
          }
          Props.allowClearAllSelections = Boolean(config?.modules.newOrder.instructions?.allowClearAllForFolding);
        }

        Props.onFormSubmit = (form) => {
          dispatch(
            updatePreferences({
              serviceLine,
              ...preferences,
              [optionType]: {
                categories: preferences[optionType].categories.map((category) => ({
                  ...category,
                  options: category.options.map((option) => ({
                    ...option,
                    value: form.includes(option.key),
                  })),
                })),
              },
            })
          );
          setIsSheetOpen(false);
        };
        break;
      case 'driverPickupInstructions':
        Props.title = isPickupAtDoor ? t('newOrderPage.bagsLocationQuestion') : t('newOrderPage.doorbellInstructionsQuestion');
        Props.preference = preferences[optionType][isPickupAtDoor ? 'atDoor' : 'inPerson'];
        Props.hideCategoryName = true;
        break;
      case 'driverDropoffInstructions':
        Props.title = isDropoffAtDoor ? t('newOrderPage.dropOffLocationQuestion') : t('newOrderPage.doorbellInstructionsQuestion');
        Props.preference = preferences[optionType][isDropoffAtDoor ? 'atDoor' : 'inPerson'];
        Props.hideCategoryName = true;
        break;
      case 'restoration':
      case 'soleIcing':
      case 'stainProtection':
        Props.preference = getshoeAddOnApprovalsItem(optionType);
        Props.hasContent = true;
        Props.alwaysHighlightOptionTitle = true;
        Props.title = t(`newOrderPage.${optionType}`);
        Props.hideCategoryName = true;
        Props.actionText = t('newOrderPage.apply');
        Props.preferenceDescription = t(`newOrderPage.${optionType}Description`);
        Props.onFormSubmit = (form) => {
          dispatch(
            updatePreferences({
              serviceLine,
              ...preferences,
              shoeAddOnApprovals: {
                ...preferences?.shoeAddOnApprovals,
                [optionType]: form.includes(optionType),
              },
            })
          );
          setIsSheetOpen(false);
        };
        break;
      default:
        /* empty */
        break;
    }

    if (optionType === 'driverDropoffInstructions' || optionType === 'driverPickupInstructions') {
      Props.onFormSubmit = (form) => {
        dispatch(
          updatePreferences({
            serviceLine,
            ...preferences,
            [optionType]: {
              ...preferences[optionType],
              ...((optionType === 'driverPickupInstructions' ? isPickupAtDoor : isDropoffAtDoor)
                ? {
                    atDoor: {
                      categories: preferences[optionType].atDoor.categories.map((category) => ({
                        ...category,
                        options: category.options.map((option) => ({
                          ...option,
                          value: form.includes(option.key),
                        })),
                      })),
                    },
                  }
                : {
                    inPerson: {
                      categories: preferences[optionType].inPerson.categories.map((category) => ({
                        ...category,
                        options: category.options.map((option) => ({
                          ...option,
                          value: form.includes(option.key),
                        })),
                      })),
                    },
                  }),
            },
          })
        );
        setIsSheetOpen(false);
      };
    }

    return createElement<OptionsFormProps>(OptionsFormSheet, Props);
  }, [
    preferences,
    isSheetOpen,
    optionType,
    optionsBSheetProps,
    t,
    shoeAddOnApprovalsPreferences,
    isPickupAtDoor,
    isDropoffAtDoor,
    config?.modules.newOrder.instructions?.supportBadgesLayoutForFolding,
    config?.modules.newOrder.instructions?.allowClearAllForFolding,
    dispatch,
    serviceLine,
  ]);

  const DriverMapper = useMemo(
    () => ({
      driverPickupInstructions: (
        <OptionItem
          title={t('newOrderPage.instructionOptions.driverPickup')}
          startIconName="icon-pickup"
          rightIcon={<RightArrowInstaShop />}
          actionType={driverPickupInstructionsPreference ? 'edit' : 'open'}
          isPreference
        />
      ),
      driverDropoffInstructions: (
        <OptionItem
          title={t('newOrderPage.instructionOptions.driverDropoff')}
          startIconName="icon-dropoff"
          rightIcon={<RightArrowInstaShop />}
          actionType={driverDropoffInstructionsPreference ? 'edit' : 'open'}
          isPreference
        />
      ),
    }),
    [driverDropoffInstructionsPreference, driverPickupInstructionsPreference, t]
  );

  const LaundryMapper = useMemo(
    () => ({
      folding: (
        <OptionItem
          title={t('newOrderPage.folding')}
          startIconName="icon-folding"
          rightIcon={<RightArrowInstaShop />}
          actionType={foldingPreference ? 'edit' : 'open'}
          isPreference
        />
      ),
      creases: (
        <OptionItem
          title={t('newOrderPage.crease')}
          startIconName="icon-crease"
          rightIcon={<RightArrowInstaShop />}
          actionType={creasesPreference ? 'edit' : 'open'}
          isPreference
        />
      ),
      starch: (
        <OptionItem
          title={t('newOrderPage.starch')}
          startIconName="icon-starch"
          rightIcon={<RightArrowInstaShop />}
          actionType={starchPreference ? 'edit' : 'open'}
          isPreference
        />
      ),
      stainDamageApprovals: (
        <OptionItem
          title={t('newOrderPage.instructionOptions.stainDamageApprovals')}
          startIconName="icon-stain-and-damage"
          rightIcon={<RightArrowInstaShop />}
          actionType={stainDamageApprovalsPreference ? 'edit' : 'open'}
          isPreference
        />
      ),
    }),
    [t, foldingPreference, creasesPreference, starchPreference, stainDamageApprovalsPreference]
  );

  const ShoesMapper = useMemo(
    () => ({
      restoration: (
        <OptionItem
          title={t('newOrderPage.restoration')}
          startIcon={<StyledRestorationIcon isStroke isActive={!shoeAddOnApprovalsPreferences?.restoration} />}
          rightIcon={<RightArrowInstaShop />}
          actionType={shoeAddOnApprovalsPreferences?.restoration ? 'edit' : 'open'}
          isPreference
        />
      ),
      soleIcing: (
        <OptionItem
          title={t('newOrderPage.soleIcing')}
          startIcon={<StyledSoleIcingIcon isStroke isActive={!shoeAddOnApprovalsPreferences?.soleIcing} />}
          rightIcon={<RightArrowInstaShop />}
          actionType={shoeAddOnApprovalsPreferences?.soleIcing ? 'edit' : 'open'}
          isPreference
        />
      ),
      stainProtection: (
        <OptionItem
          title={t('newOrderPage.stainProtection')}
          startIcon={<StyledStainProtectionIcon isRemoveStroke isActive={!shoeAddOnApprovalsPreferences?.stainProtection} />}
          rightIcon={<RightArrowInstaShop />}
          actionType={shoeAddOnApprovalsPreferences?.stainProtection ? 'edit' : 'open'}
          isPreference
        />
      ),
    }),
    [t, shoeAddOnApprovalsPreferences?.restoration, shoeAddOnApprovalsPreferences?.soleIcing, shoeAddOnApprovalsPreferences?.stainProtection]
  );

  const ServiceLineOptions = useMemo(
    () => () => {
      const options = {
        [ServiceLine.LAUNDRY_SERVICE]: LaundryMapper,
        [ServiceLine.SHOE_SERVICE]: ShoesMapper,
        [ServiceLine.WASH_BAG]: {},
        [ServiceLine.FINERY]: {},
      };

      return options[serviceLine!!] ?? { ...LaundryMapper, ...DriverMapper };
    },
    [DriverMapper, LaundryMapper, ShoesMapper, serviceLine]
  );

  const handleOptionSelected = useCallback((key: keyof Preferences.CustomerPreferences) => {
    setOptionType(key);
    setIsSheetOpen(true);
  }, []);

  return (
    <div>
      {serviceLine && hasMultipleServicesSelected && (
        <StyledH4 className="font-weight-light mt-5">{t(`newOrderPage.${serviceLine}InstructionHeading`)}</StyledH4>
      )}
      <OptionsRenderer clickHandler={handleOptionSelected} mapperObj={{ ...ServiceLineOptions() }} serviceLine={serviceLine} />
      {renderSheet()}
    </div>
  );
};

export default OrderInstructionsOptions;
